import _ from 'lodash';
import constants from '#packages/constants';

import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef, SerializedCompStructure } from 'types/documentServices';
import { designData as designDataUtils } from '#packages/util';

const NON_DUPLICATEABLE_COMPONENT_TYPES = new Set([
  constants.COMP_TYPES.STRIP_COLUMNS_CONTAINER,
  constants.COMP_TYPES.TPA_WIDGET,
  constants.COMP_TYPES.TPA_MULTI_SECTION,
  constants.COMP_TYPES.TPA_SECTION,
  constants.COMP_TYPES.APP_WIDGET,
]);

function validateProportionsArraySum(sum: number) {
  if (sum === 0) {
    throw new Error(
      'proportionItems array cannot be empty or contain only zeros',
    );
  }
}

function toPercentages(array: number[]) {
  const sum = _.sum(array);

  validateProportionsArraySum(sum);
  return array.map((item) => (100 * item) / sum);
}

function get980Proportions(proportionItem: number[]) {
  const total = _.sum(proportionItem);

  validateProportionsArraySum(total);
  const chunk = 980 / total;
  return proportionItem.map((proportion: number) => proportion * chunk);
}

function getColumnsWidthProportionsMap(
  editorAPI: EditorAPI,
  columnsContainerPtr: CompRef,
) {
  const columns = editorAPI.components.getChildren(columnsContainerPtr);

  const res = _(columns)
    .keyBy('id')
    .mapValues(editorAPI.components.layout.get)
    .mapValues('width')
    .value();

  const arrSum = Object.values(res).reduce((acc, val) => acc + val, 0);

  return _.mapValues(res, (v) => Math.round((100 * v) / arrSum));
}

function moveToIndex(
  editorAPI: EditorAPI,
  compPointer: CompRef,
  index: number,
  dontAddToUndoRedoStack: boolean,
) {
  if (index !== undefined) {
    editorAPI.components.arrangement.moveToIndex(compPointer, index, {
      dontAddToUndoRedoStack,
    });
  }
}

function removeFullWidthChildrenFromStructure(
  editorAPI: EditorAPI,
  compStructure: SerializedCompStructure,
) {
  compStructure.components = _.reject(
    compStructure.components,
    editorAPI.components.is.fullWidthByStructure,
  );

  compStructure.components.forEach((comp) =>
    removeFullWidthChildrenFromStructure(editorAPI, comp),
  );

  return compStructure;
}

function removeNonDuplicatableComponentsFromStructure(
  compStructure: SerializedCompStructure,
) {
  compStructure.components = compStructure.components.filter(
    (compStructure: SerializedCompStructure) =>
      !NON_DUPLICATEABLE_COMPONENT_TYPES.has(compStructure.componentType),
  );

  compStructure.components.forEach(
    removeNonDuplicatableComponentsFromStructure,
  );

  return compStructure;
}

function getColumnResizeRatio(
  currentProportion: number[],
  previousProportion: number[],
  index: number,
) {
  const curr = currentProportion[index];
  const prev = previousProportion[index];

  return curr && prev ? curr / prev : null;
}

function toProportionsIntegers(newProportions: number[]) {
  const roundedProportions = newProportions.map(Math.floor);
  const proportionsSum = roundedProportions.reduce(
    (acc, value) => acc + value,
    0,
  );
  const remainingPercentToAdd = 100 - proportionsSum;

  if (remainingPercentToAdd) {
    roundedProportions[0] += remainingPercentToAdd;
  }

  return roundedProportions;
}

const resetStripStyleIfNeeded = (editorAPI: EditorAPI, stripRef: CompRef) => {
  if (!editorAPI.columns.isStripWithSingleColumn(stripRef)) {
    return;
  }

  const stripStyle = editorAPI.components.style.get(stripRef);
  const hasDividersDesign = designDataUtils.hasDividersDesign(
    editorAPI,
    stripRef,
  );

  const isStripStyleNeedToBeReset =
    !hasDividersDesign &&
    stripStyle.skin === constants.SKINS.DESIGNED_STRIP_CONTAINER;

  if (!isStripStyleNeedToBeReset) {
    return;
  }

  resetStripDesign(editorAPI, stripRef);
};

const resetColumnStyleIfNeeded = (editorAPI: EditorAPI, stripRef: CompRef) => {
  if (!editorAPI.columns.isStripWithSingleColumn(stripRef)) {
    return;
  }

  const leftColumnRef = editorAPI.components.getChildren(stripRef)[0];
  const columnStyle = editorAPI.components.style.get(leftColumnRef);
  const hasDividersDesign = designDataUtils.hasDividersDesign(
    editorAPI,
    stripRef,
  );

  const isColumnStyleNeedToBeReset =
    hasDividersDesign && columnStyle.skin === constants.SKINS.DESIGNED_COLUMN;

  if (!isColumnStyleNeedToBeReset) {
    return;
  }

  resetColumnDesign(editorAPI, leftColumnRef);
};

const resetColumnDesign = (editorAPI: EditorAPI, columnRef: CompRef) => {
  const columnStyle = editorAPI.components.style.get(columnRef);
  columnStyle.style = constants.COLUMN.getBaseStyledColumnStyle();

  editorAPI.components.style.update(columnRef, columnStyle, true);
};

const resetStripDesign = (editorAPI: EditorAPI, stripRef: CompRef) => {
  const stripStyle = editorAPI.components.style.get(stripRef);
  stripStyle.style = constants.STRIP.getBaseStyledStripStyle();

  editorAPI.components.style.update(stripRef, stripStyle, true);
};

export {
  toPercentages,
  get980Proportions,
  getColumnsWidthProportionsMap,
  moveToIndex,
  removeFullWidthChildrenFromStructure,
  removeNonDuplicatableComponentsFromStructure,
  getColumnResizeRatio,
  toProportionsIntegers,
  resetColumnStyleIfNeeded,
  resetStripStyleIfNeeded,
  resetStripDesign,
  resetColumnDesign,
};
